import React, {
    FC,
    MouseEvent,
    useState,
    useEffect,
    useMemo,
    ReactNode,
    useCallback,
    useRef,
} from "react";
import { useHistory } from "react-router-dom";
import memoize from "memoize-one";
import { useMobileMode } from "Shared/util/custom-hooks";
import { directions, sortByColumn, getNewDirection } from "Shared/util/sort";
import styles from "./data-table.css";
import DataTableFooter from "./data-table-footer";
import { DataTableHeader } from "./data-table-header";
import { DataTableRows } from "./data-table-rows";

type NestedKeyOf<ObjectType extends object> = {
    [Key in keyof ObjectType &
        (string | number)]: ObjectType[Key] extends object
        ? ObjectType[Key] extends Array<any>
            ? `${Key}`
            : `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
        : `${Key}`;
}[keyof ObjectType & (string | number)];

export enum ColumnTypes {
    Amount = "AMOUNT",
    Date = "DATE",
}

export type DataTableColumn<TRowData extends object = any> = {
    customCellRenderer?: (row: TRowData) => ReactNode;
    footer?: {
        groupBy?: boolean;
        sum?: boolean;
    };
    formatter?: (value: any, row: TRowData) => string;
    hiddenMobile?: boolean;
    key: NestedKeyOf<TRowData>;
    oneLineEllipsis?: boolean;
    title?: string;
    type?: ColumnTypes;
};

export type DataTableRow<TRowData = any> = TRowData;

export type SelectableRowHeader = {
    hideSelectAll: boolean,
    title: string
}

export type DataTableProps = {
    columns: DataTableColumn[];
    getRowId?: (row: DataTableRow) => string; // Info (240912): Should aim to become mandatory at some point.
    getRowLink?: (row: DataTableRow) => void;
    isSelected?: (row: DataTableRow) => boolean;
    loading?: boolean;
    loadingBottomText?: string;
    loadingTopText?: string;
    onRowDeselect?: (row: DataTableRow) => void;
    onRowSelect?: (row: DataTableRow) => void;
    onSelectedRowsChanged?: (rows: DataTableRow[]) => void;
    pageSize?: number;
    paging?: boolean;
    preSelectedRows?: boolean | ((row: DataTableRow) => boolean);
    rowInformationMessage?: (row: DataTableRow) => void;
    rows: DataTableRow[];
    selectableRowDisabled?: (row: DataTableRow) => boolean;
    selectableRowHeader?: SelectableRowHeader;
    selectableRowHidden?: (row: DataTableRow) => boolean;
    sorting?: boolean;
    useRowInformation?: boolean;
    useSelectableRows?: boolean;
};

const getColumnTypeClass = (columnType: ColumnTypes | undefined) => {
    switch (columnType) {
        case ColumnTypes.Amount:
            return styles.amount;
        case ColumnTypes.Date:
            return styles.date;
        default:
            return "";
    }
};

const isInvertedColumn = (type: ColumnTypes | undefined) =>
    type === ColumnTypes.Amount;

const pageRows = memoize((rows, pageSize, currentPage) => {
    const start = pageSize * (currentPage - 1);
    const end = pageSize * currentPage;
    return rows.slice(start, end);
});

// https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#what-about-memoization
const sortRows = memoize((rows, sortKey, sortDirection) =>
    sortByColumn(rows, sortKey, sortDirection),
);

const filterValidRows = (
    rows: DataTableProps["rows"],
    selectableRowHidden: DataTableProps["selectableRowHidden"],
) =>
    !selectableRowHidden
        ? rows
        : (rows || []).filter((x) => !selectableRowHidden(x));

const DataTable: FC<DataTableProps> = ({
    columns,
    getRowId,
    getRowLink,
    loading = false,
    loadingBottomText,
    loadingTopText,
    isSelected,
    onRowDeselect,
    onRowSelect,
    onSelectedRowsChanged,
    pageSize = 20,
    paging = true,
    preSelectedRows = false,
    rows,
    rowInformationMessage,
    selectableRowHeader = {hideSelectAll: false, title: ""},
    selectableRowHidden,
    selectableRowDisabled,
    sorting = true,
    useRowInformation = false,
    useSelectableRows = false,
}) => {
    const { push } = useHistory();
    const isMobileMode = useMobileMode();

    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("");
    const [sortDirection, setSortDirection] = useState(directions.ASC);
    const [showAllRows, setShowAllRows] = useState(false);
    const [selectedRows, setSelectedRows] = useState<DataTableRow[]>([]);

    const selectableRows = useMemo(
        () => filterValidRows(rows, selectableRowHidden),
        [rows, selectableRowHidden],
    );

    const initialLoaded = useRef(false);
    useEffect(() => {
        const isInitial = !!selectableRows?.length && !initialLoaded.current;
        if (isInitial) {
            initialLoaded.current = true;

            setCurrentPage(1);
            setShowAllRows(false);

            if (preSelectedRows) {
                setSelectedRows(
                    typeof preSelectedRows === "function"
                        ? selectableRows.filter((x) => preSelectedRows(x))
                        : selectableRows,
                );
            }
        } else if (isSelected) {
            setSelectedRows(selectableRows.filter((x) => isSelected(x)));
        }
    }, [isSelected, preSelectedRows, selectableRows]);

    useEffect(() => {
        onSelectedRowsChanged?.(selectedRows);
    }, [selectedRows]);

    const handleSelectAllRows = useCallback(() => {
        setSelectedRows(selectableRows);
    }, [selectableRows]);

    const handleDeselectAllRows = useCallback(() => {
        setSelectedRows([]);
    }, []);

    const handleSelectRow = useCallback(
        (row: []) => {
            onRowSelect?.(row);
            setSelectedRows((prev) => [...prev, row]);
        },
        [onRowSelect],
    );

    const handleDeselectRow = useCallback(
        (row: DataTableRow) => {
            onRowDeselect?.(row);
            // Info (240912): Temp if/else-solution until all DataTables have implemented the 'getRowId' function.
            if (getRowId) {
                setSelectedRows((prev) =>
                    prev.filter((x) => getRowId(x) !== getRowId(row)),
                );
            } else {
                setSelectedRows((prev) => prev.filter((x) => x !== row));
            }
        },
        [getRowId, onRowDeselect],
    );

    const handleSorting = useCallback(
        (column: DataTableColumn["key"]) => {
            if (!sorting) return;
            const newDirection = getNewDirection(
                sortKey,
                column,
                sortDirection,
            );
            setSortDirection(newDirection);
            setSortKey(column);
        },
        [sortDirection, sortKey, sorting],
    );

    const handleRowClick = useCallback(
        (event?: MouseEvent<HTMLTableRowElement>) => {
            const path = event?.currentTarget?.getAttribute("data-href");
            if (path) {
                push(path);
            }
        },
        [push],
    );

    const handlePaging = (page: number) => setCurrentPage(page);

    const handleShowAllRows = (show: boolean) => setShowAllRows(show);

    let displayRows: any[] = sortRows(rows, sortKey, sortDirection);

    if (paging && !showAllRows) {
        displayRows = pageRows(displayRows, pageSize, currentPage);
    }

    return (
        <div className={styles.dataTable}>
            <table>
                <thead>
                    <DataTableHeader
                        columns={columns}
                        handleSorting={handleSorting}
                        selectableRowHeader={selectableRowHeader}
                        isInvertedColumn={isInvertedColumn}
                        numberOfRows={displayRows.length}
                        numberOfSelectedRows={selectedRows.length}
                        numberOfValidRows={selectableRows.length}
                        onAllRowsDeselected={handleDeselectAllRows}
                        onAllRowsSelected={handleSelectAllRows}
                        sortDirection={sortDirection}
                        sortKey={sortKey}
                        sorting={sorting}
                        useRowInformation={useRowInformation}
                        useSelectableRows={useSelectableRows}
                    />
                </thead>
                <tbody>
                    <DataTableRows
                        columns={columns}
                        getColumnTypeClass={getColumnTypeClass}
                        getRowId={getRowId}
                        getRowLink={getRowLink}
                        handleRowClick={handleRowClick}
                        isMobileMode={isMobileMode}
                        isSelectableDisabled={selectableRowDisabled}
                        loading={loading}
                        loadingBottomText={loadingBottomText}
                        loadingTopText={loadingTopText}
                        onRowDeselected={handleDeselectRow}
                        onRowSelected={handleSelectRow}
                        rowInformationMessage={rowInformationMessage}
                        rows={displayRows}
                        selectedRows={selectedRows}
                        useRowInformation={useRowInformation}
                        useSelectableRows={useSelectableRows}
                        validRows={selectableRows}
                    />
                </tbody>
                {rows.length > 0 && (
                    <tfoot>
                        <DataTableFooter
                            columns={columns}
                            currentPage={currentPage}
                            handlePaging={handlePaging}
                            handleShowAllRows={handleShowAllRows}
                            isInvertedColumn={isInvertedColumn}
                            isMobileMode={isMobileMode}
                            loading={loading}
                            pageSize={pageSize}
                            paging={paging}
                            rows={rows}
                            showAllRows={showAllRows}
                            useRowInformation={useRowInformation}
                            useSelectableRows={useSelectableRows}
                        />
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default DataTable;
